import {
  forwardRef,
  type HTMLAttributes,
  type ReactNode,
  createContext,
  useContext,
  useState,
  useCallback,
  useId,
  Children,
  isValidElement,
  cloneElement,
} from 'react';
import { motion, AnimatePresence, LayoutGroup, type HTMLMotionProps } from 'framer-motion';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

/**
 * Utility function to merge Tailwind classes
 */
const cn = (...inputs: ClassValue[]) => twMerge(clsx(inputs));

/**
 * Tab variants
 */
type TabVariant = 'line' | 'pills' | 'enclosed';

/**
 * Tab sizes
 */
type TabSize = 'sm' | 'md' | 'lg';

/**
 * Context value for tabs
 */
interface TabsContextValue {
  activeTab: string;
  setActiveTab: (value: string) => void;
  variant: TabVariant;
  size: TabSize;
  tabsId: string;
}

const TabsContext = createContext<TabsContextValue | null>(null);

/**
 * Hook to access tabs context
 */
const useTabs = () => {
  const context = useContext(TabsContext);
  if (!context) {
    throw new Error('Tab components must be used within a Tabs component');
  }
  return context;
};

/**
 * Props for the Tabs component
 */
export interface TabsProps extends Omit<HTMLAttributes<HTMLDivElement>, 'onChange'> {
  /** Currently active tab value */
  value?: string;
  /** Default active tab value (for uncontrolled mode) */
  defaultValue?: string;
  /** Callback when active tab changes */
  onChange?: (value: string) => void;
  /** Tab visual variant */
  variant?: TabVariant;
  /** Tab size */
  size?: TabSize;
  /** Tab content */
  children: ReactNode;
}

/**
 * Props for TabList component
 */
export interface TabListProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

/**
 * Props for Tab component
 */
export interface TabProps extends HTMLAttributes<HTMLButtonElement> {
  /** Unique value for this tab */
  value: string;
  /** Tab label content */
  children: ReactNode;
  /** Disable the tab */
  disabled?: boolean;
  /** Icon to display before label */
  icon?: ReactNode;
}

/**
 * Props for TabPanels component
 */
export interface TabPanelsProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

/**
 * Props for TabPanel component
 */
export interface TabPanelProps extends HTMLAttributes<HTMLDivElement> {
  /** Corresponding tab value */
  value: string;
  /** Panel content */
  children: ReactNode;
}

/**
 * Size styles for tabs
 */
const sizeStyles: Record<TabSize, { tab: string; list: string }> = {
  sm: {
    tab: 'px-3 py-1.5 text-sm gap-1.5',
    list: 'gap-1',
  },
  md: {
    tab: 'px-4 py-2 text-base gap-2',
    list: 'gap-1.5',
  },
  lg: {
    tab: 'px-5 py-2.5 text-lg gap-2.5',
    list: 'gap-2',
  },
};

/**
 * TabList component - container for Tab buttons
 */
export const TabList = forwardRef<HTMLDivElement, TabListProps>(
  ({ children, className, ...props }, ref) => {
    const { variant, size, tabsId } = useTabs();

    return (
      <div
        ref={ref}
        role="tablist"
        className={cn(
          'flex items-center',
          sizeStyles[size].list,
          variant === 'line' && 'border-b border-white/[0.08]',
          variant === 'enclosed' &&
            'p-1 backdrop-blur-xl bg-white/[0.06] border border-white/[0.10] rounded-xl',
          variant === 'pills' && 'gap-2',
          className
        )}
        {...props}
      >
        <LayoutGroup id={tabsId}>
          {children}
        </LayoutGroup>
      </div>
    );
  }
);

TabList.displayName = 'TabList';

/**
 * Tab component - individual tab button
 */
export const Tab = forwardRef<HTMLButtonElement, TabProps>(
  ({ value, children, disabled, icon, className, ...props }, ref) => {
    const { activeTab, setActiveTab, variant, size, tabsId } = useTabs();
    const isActive = activeTab === value;

    const handleClick = () => {
      if (!disabled) {
        setActiveTab(value);
      }
    };

    return (
      <button
        ref={ref}
        role="tab"
        type="button"
        aria-selected={isActive}
        aria-controls={`${tabsId}-panel-${value}`}
        id={`${tabsId}-tab-${value}`}
        tabIndex={isActive ? 0 : -1}
        disabled={disabled}
        onClick={handleClick}
        className={cn(
          'relative inline-flex items-center justify-center',
          'font-medium whitespace-nowrap',
          'transition-colors duration-200',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900',
          sizeStyles[size].tab,
          // Variant styles
          variant === 'line' && [
            '-mb-px',
            isActive ? 'text-primary' : 'text-surface-500 hover:text-surface-800',
          ],
          variant === 'pills' && [
            'rounded-lg',
            isActive
              ? 'text-white'
              : 'text-surface-500 hover:text-surface-800 hover:bg-white/[0.06]',
          ],
          variant === 'enclosed' && [
            'rounded-lg',
            isActive
              ? 'text-white'
              : 'text-surface-500 hover:text-surface-800',
          ],
          disabled && 'opacity-50 cursor-not-allowed',
          className
        )}
        {...props}
      >
        {/* Active indicator */}
        {isActive && (
          <motion.span
            layoutId={`${tabsId}-indicator`}
            className={cn(
              'absolute inset-0',
              variant === 'line' && 'top-auto h-0.5 bg-primary rounded-full',
              variant === 'pills' &&
                'backdrop-blur-xl bg-primary/80 border border-primary/40 rounded-lg -z-10',
              variant === 'enclosed' &&
                'backdrop-blur-xl bg-white/[0.12] border border-white/[0.15] rounded-lg -z-10'
            )}
            transition={{ type: 'spring', stiffness: 400, damping: 30 }}
          />
        )}

        {icon && <span className="flex-shrink-0">{icon}</span>}
        <span>{children}</span>
      </button>
    );
  }
);

Tab.displayName = 'Tab';

/**
 * TabPanels component - container for TabPanel components
 */
export const TabPanels = forwardRef<HTMLDivElement, TabPanelsProps>(
  ({ children, className, ...props }, ref) => {
    const { activeTab } = useTabs();

    return (
      <div ref={ref} className={cn('mt-4', className)} {...props}>
        <AnimatePresence mode="wait">
          {Children.map(children, (child) => {
            if (isValidElement<TabPanelProps>(child) && child.props.value === activeTab) {
              return cloneElement(child, { key: activeTab });
            }
            return null;
          })}
        </AnimatePresence>
      </div>
    );
  }
);

TabPanels.displayName = 'TabPanels';

/**
 * TabPanel component - individual tab content panel
 */
export const TabPanel = forwardRef<HTMLDivElement, TabPanelProps>(
  ({ value, children, className, ...props }, ref) => {
    const { tabsId } = useTabs();

    return (
      <motion.div
        ref={ref}
        role="tabpanel"
        id={`${tabsId}-panel-${value}`}
        aria-labelledby={`${tabsId}-tab-${value}`}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        transition={{ duration: 0.2 }}
        className={cn('focus:outline-none', className)}
        tabIndex={0}
        {...(props as Omit<HTMLMotionProps<'div'>, 'ref'>)}
      >
        {children}
      </motion.div>
    );
  }
);

TabPanel.displayName = 'TabPanel';

/**
 * Tabs component with Liquid Glass design
 *
 * @example
 * ```tsx
 * <Tabs defaultValue="overview" variant="pills">
 *   <TabList>
 *     <Tab value="overview">Overview</Tab>
 *     <Tab value="analytics">Analytics</Tab>
 *     <Tab value="settings">Settings</Tab>
 *   </TabList>
 *   <TabPanels>
 *     <TabPanel value="overview">Overview content</TabPanel>
 *     <TabPanel value="analytics">Analytics content</TabPanel>
 *     <TabPanel value="settings">Settings content</TabPanel>
 *   </TabPanels>
 * </Tabs>
 * ```
 */
export const Tabs = forwardRef<HTMLDivElement, TabsProps>(
  (
    {
      value,
      defaultValue,
      onChange,
      variant = 'line',
      size = 'md',
      children,
      className,
      ...props
    },
    ref
  ) => {
    const tabsId = useId();
    const [internalValue, setInternalValue] = useState(defaultValue || '');

    const activeTab = value !== undefined ? value : internalValue;

    const setActiveTab = useCallback(
      (newValue: string) => {
        if (value === undefined) {
          setInternalValue(newValue);
        }
        onChange?.(newValue);
      },
      [value, onChange]
    );

    return (
      <TabsContext.Provider
        value={{
          activeTab,
          setActiveTab,
          variant,
          size,
          tabsId,
        }}
      >
        <div ref={ref} className={cn('w-full', className)} {...props}>
          {children}
        </div>
      </TabsContext.Provider>
    );
  }
);

Tabs.displayName = 'Tabs';

export default Tabs;
